import i18n from "@/i18n.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export type ApiTimeOffCategoryPayload = Omit<
  ApiTimeOffCategory,
  "id" | "policies_count" | "policies"
>;

export interface ApiTimeOffCategoryShort {
  id: number;
  title: string;
  description?: string;
  policies_count: number;
  emoji: string;
  color?: string;
  period_track_type: ApiTimeOffPeriodTrackingTime;
  category_type: ApiTimeOffCategoryType;
  is_parental_leave: boolean;
}

export interface ApiTimeOffCategory extends ApiTimeOffCategoryShort {
  paid_type: ApiTimeOffPaidType;
  is_public: boolean;
  policies: ApiTimeOffCategoryPolicy[];
}

export interface ApiTimeOffCategoryPolicy extends ApiTimeOffPolicy {
  taken_by_upper_employee_ids: number[];
  is_all_active_employees_only: boolean;
}

export interface ApiTimeOffPolicyTransition {
  from_policy_id: number | null;
  to_policy_id: number | null;
  employee_ids: number[];
  date_effective_from: Date;
}

export interface ApiTimeOffPolicyAssignToPreviewPayload {
  category_id: number;
  policy_id: number | null;
  assign_to_groups: RuleGroupShort[];
}

export interface ApiTimeOffPolicyAssignToPreview {
  employee_ids: number[];
  taken_by_upper_employee_ids: number[];
}

export enum ApiTimeOffPeriodTrackingTime {
  days = "days",
  hours = "hours",
}

export enum ApiTimeOffCategoryType {
  accrues_time = "accrues_time",
  flexible = "flexible",
  manual = "manual",
}

export enum ApiTimeOffPaidType {
  not_working_paid = "not_working_paid",
  working_paid = "working_paid",
  not_working_unpaid = "not_working_unpaid",
}

export enum ApiTimeOffCountDaysType {
  working_days = "business_days",
  calendar_days = "calendar_days",
}

export enum ApiTimeOffDayAmountType {
  full_day = "full_day",
  work_pattern_share = "work_pattern_share",
}

export interface ApiTimeOffPolicyVersion {
  id: number;
  date_effective_from: string | null;
  created_by: ApiEmployee | null;
  assign_to_groups?: RuleGroup[] | null;
  assign_to?: Rule[] | null;
  milestones: ApiTimeOffPolicyMilestone[];

  carryover_cycle_date_type: ApiTimeOffPolicyCarryoverCycleDateType | null;
  carryover_cycle_date_data: ApiTimeOffCarryoverCycleDateData | null;
  carryover_expire_enable: boolean;
  carryover_expire_date_type: ApiTimeOffDateType | null;
  carryover_expire_date_value: ApiTimeOffCarryoverExpireDateValue | null;
  carryover_enabled: boolean;
  carryover_amount: number | null;

  start_balance_type: ApiTimeOffPolicyStartBalanceType | null;
  termination_accrual_type: ApiTimeOffPolicyTerminationAccrualType;
  milestone_transfer_type: ApiTimeOffPolicyMilestoneTransferType | null;
  contractual_fte_proration_enabled: boolean;

  employees_count: number;
}

export function getTimeOffPolicyAssignToGroups(version: ApiTimeOffPolicyVersion): RuleGroup[] {
  if (version.assign_to_groups != null) return version.assign_to_groups;
  const rules = version.assign_to ?? [];
  return rules.length > 0 ? [{ rules }] : [];
}

export interface ApiTimeOffPolicy {
  id: number;
  category_id: number;
  title: string;

  limits_negative_balance_allowed: boolean;
  negative_balance_in_cycle_allowed: boolean;
  min_daily_amount: number | null;
  min_request_total: number | null;
  max_request_total: number | null;
  min_notice_period: number | null;
  max_notice_period: number | null;
  notice_period_days_type: ApiTimeOffCountDaysType;
  attachment_required_from_request_total: number | null;
  attachment_required_from_cycle_total: number | null;
  overlap_requests_allowed: boolean;
  probation_requests_allowed: boolean;
  past_requests_allowed: boolean;

  accrual_confirm_required: boolean;
  count_days_type: ApiTimeOffCountDaysType;
  day_amount_type: ApiTimeOffDayAmountType;

  versions: ApiTimeOffPolicyVersion[];

  employees_count: number;
}

export interface ApiTimeOffPolicyVersionPayload {
  assign_to_groups: RuleGroupShort[];
  milestones: ApiTimeOffPolicyMilestone[];

  carryover_cycle_date_type: ApiTimeOffPolicyCarryoverCycleDateType | null;
  carryover_cycle_date_data: ApiTimeOffCarryoverCycleDateData | null;
  carryover_expire_enable: boolean;
  carryover_expire_date_type: ApiTimeOffDateType | null;
  carryover_expire_date_value: ApiTimeOffCarryoverExpireDateValue | null;
  carryover_enabled: boolean;
  carryover_amount: number | null;

  start_balance_type: ApiTimeOffPolicyStartBalanceType;
  termination_accrual_type: ApiTimeOffPolicyTerminationAccrualType;
  milestone_transfer_type: ApiTimeOffPolicyMilestoneTransferType;
  contractual_fte_proration_enabled: boolean;

  date_effective_from?: string | null;
}

export interface ApiTimeOffPolicyVersionUpdatePayload {
  version_id: number;
  version_update: ApiTimeOffPolicyVersionPayload;
}

export interface ApiTimeOffPolicyPayload {
  category_id: number;
  title: string;

  limits_negative_balance_allowed: boolean;
  negative_balance_in_cycle_allowed: boolean;
  min_daily_amount: number | null;
  min_request_total: number | null;
  max_request_total: number | null;
  min_notice_period: number | null;
  max_notice_period: number | null;
  notice_period_days_type: ApiTimeOffCountDaysType;
  attachment_required_from_request_total: number | null;
  attachment_required_from_cycle_total: number | null;
  overlap_requests_allowed: boolean;
  probation_requests_allowed: boolean;
  past_requests_allowed: boolean;

  accrual_confirm_required: boolean;
  count_days_type: ApiTimeOffCountDaysType;
  day_amount_type: ApiTimeOffDayAmountType;

  new_version: ApiTimeOffPolicyVersionPayload | null;
  update_version: ApiTimeOffPolicyVersionUpdatePayload | null;
}

export interface ApiTimeOffPolicyMilestone {
  title: string;
  start_date_type: ApiTimeOffDateType;
  start_amount: number;
  start_balance_amount: number;
  accrual_frequency_type: ApiTimeOffAccrualFrequencyTypes;
  accrual_frequency_data?: ApiTimeOffAccrualFrequencyData | null;
  accrual_amount: number;
  accrual_at_type: ApiTimeOffPolicyAccrualAtType;
  accrual_cap_enabled: boolean;
  accrual_cap_amount: number;
}

export interface ApiTimeOffAccrualFrequencyData {
  date_first?: Date | null | undefined;
  date_second?: Date | null | undefined;
}

export interface ApiTimeOffCarryoverCycleDateData {
  date_first?: Date | null | undefined;
}

export interface ApiTimeOffCarryoverExpireDateValue {
  count: number | null | undefined;
}

export enum ApiTimeOffPolicyCarryoverCycleDateType {
  specific_date = "specific_date",
  hire_date = "hire_date",
  quarter_start = "quarter_start",
}

export enum ApiTimeOffPolicyCarryoverExpireDateType {
  days = "days",
  hours = "hours",
}

export enum ApiTimeOffPolicyStartBalanceType {
  proportional_accrual = "proportional_accrual",
  full_accrual_amount = "full_accrual_amount",
}

export enum ApiTimeOffPolicyTerminationAccrualType {
  proportional_accrual = "proportional_accrual",
  no_partial_accrual = "no_partial_accrual",
}

export enum ApiTimeOffPolicyMilestoneTransferType {
  immediately_on_condition = "immediately_on_condition",
  end_of_accrual_period = "end_of_accrual_period",
}

export enum ApiTimeOffPolicyAccrualAtType {
  start_of_accrual_period = "start_of_accrual_period",
  end_of_accrual_period = "end_of_accrual_period",
}

export enum ApiTimeOffDateType {
  day = "day",
  week = "week",
  month = "month",
  year = "year",
}

export enum ApiTimeOffAccrualFrequencyTypes {
  daily = "daily",
  weekly = "weekly",
  biweekly = "biweekly",
  semimonthly = "semimonthly",
  monthly = "monthly",
  monthly_annual_proportional = "monthly_annual_proportional",
  hire_date_monthly = "hire_date_monthly",
  quarterly = "quarterly",
  semiannual = "semiannual",
  yearly = "yearly",
  hire_date = "hire_date",
  hire_date_monthly_annual_proportional = "hire_date_monthly_annual_proportional",
}

export enum ApiTimeOffPolicyMilestoneCarryoverType {
  none = "none",
  up_to = "up_to",
  unlimited = "unlimited",
}

export const findCurrentTimeOffPolicyVersion = (
  versions: ApiTimeOffPolicyVersion[]
): ApiTimeOffPolicyVersion | undefined => {
  const todayMs = new Date().setHours(0, 0, 0, 0);
  return versions.find((v) => {
    if (!v.date_effective_from) return true;
    const parsed = new Date(v.date_effective_from).getTime();
    return !Number.isNaN(parsed) && parsed <= todayMs;
  });
};

export const getPaidTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:paid_type_working_paid_title"),
      caption: i18n.t("time_off:paid_type_working_paid_description"),
      id: ApiTimeOffPaidType.working_paid,
    },
    {
      title: i18n.t("time_off:paid_type_not_working_paid_title"),
      caption: i18n.t("time_off:paid_type_not_working_paid_description"),
      id: ApiTimeOffPaidType.not_working_paid,
    },
    {
      title: i18n.t("time_off:paid_type_not_working_unpaid_title"),
      caption: i18n.t("time_off:paid_type_not_working_unpaid_description"),
      id: ApiTimeOffPaidType.not_working_unpaid,
    },
  ];
};
export const getTimeTrackingTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:days"),
      id: ApiTimeOffPeriodTrackingTime.days,
    },
    {
      title: i18n.t("time_off:hours"),
      id: ApiTimeOffPeriodTrackingTime.hours,
    },
  ];
};
export const getCountDaysTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("core:calendar_days"),
      caption: i18n.t("time_off:count_days_types_calendar_days_description"),
      id: ApiTimeOffCountDaysType.calendar_days,
    },
    {
      title: i18n.t("core:working_days"),
      caption: i18n.t("time_off:count_days_types_working_days_description"),
      id: ApiTimeOffCountDaysType.working_days,
    },
  ];
};
export const getCategoriesTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:category_type_accrues_time_title"),
      caption: i18n.t("time_off:category_type_accrues_time_description"),
      id: ApiTimeOffCategoryType.accrues_time,
    },
    {
      title: i18n.t("time_off:category_type_flexible_title"),
      caption: i18n.t("time_off:category_type_flexible_description"),
      id: ApiTimeOffCategoryType.flexible,
    },
    {
      title: i18n.t("time_off:category_type_manual_title"),
      caption: i18n.t("time_off:category_type_manual_description"),
      id: ApiTimeOffCategoryType.manual,
    },
  ];
};
export const getCarryoverDateTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:carryover_date_hire_title"),
      id: ApiTimeOffPolicyCarryoverCycleDateType.hire_date,
    },
    {
      title: i18n.t("time_off:carryover_date_quarter_title"),
      id: ApiTimeOffPolicyCarryoverCycleDateType.quarter_start,
    },
    {
      title: i18n.t("time_off:carryover_date_specific_title"),
      id: ApiTimeOffPolicyCarryoverCycleDateType.specific_date,
    },
  ];
};
export const getCarryoverExpiringDateTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:carryover_expiration_date_day_title"),
      id: ApiTimeOffDateType.day,
    },
    {
      title: i18n.t("time_off:carryover_expiration_date_week_title"),
      id: ApiTimeOffDateType.week,
    },
    {
      title: i18n.t("time_off:carryover_expiration_date_month_title"),
      id: ApiTimeOffDateType.month,
    },
    {
      title: i18n.t("time_off:carryover_expiration_date_year_title"),
      id: ApiTimeOffDateType.year,
    },
  ];
};

export const getAccrualAtTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:accrual_at_type_at_start_title"),
      id: ApiTimeOffPolicyAccrualAtType.start_of_accrual_period,
    },
    {
      title: i18n.t("time_off:accrual_at_type_at_end_title"),
      id: ApiTimeOffPolicyAccrualAtType.end_of_accrual_period,
    },
  ];
};

export const getAccrualFrequencyTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:milestone_accrual_date_type_daily"),
      id: ApiTimeOffAccrualFrequencyTypes.daily,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_weekly"),
      id: ApiTimeOffAccrualFrequencyTypes.weekly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_biweekly"),
      id: ApiTimeOffAccrualFrequencyTypes.biweekly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_semimonthly"),
      id: ApiTimeOffAccrualFrequencyTypes.semimonthly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_monthly"),
      id: ApiTimeOffAccrualFrequencyTypes.monthly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_hire_date_monthly"),
      id: ApiTimeOffAccrualFrequencyTypes.hire_date_monthly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_quarterly"),
      id: ApiTimeOffAccrualFrequencyTypes.quarterly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_semiannual"),
      id: ApiTimeOffAccrualFrequencyTypes.semiannual,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_yearly"),
      id: ApiTimeOffAccrualFrequencyTypes.yearly,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_hire_date"),
      id: ApiTimeOffAccrualFrequencyTypes.hire_date,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_monthly_annual_proportional"),
      id: ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional,
    },
    {
      title: i18n.t("time_off:milestone_accrual_date_type_hire_date_monthly_annual_proportional"),
      id: ApiTimeOffAccrualFrequencyTypes.hire_date_monthly_annual_proportional,
    },
  ];
};
export const getMilestoneCarryoverTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:milestone_carryover_none_title"),
      caption: i18n.t("time_off:milestone_carryover_none_caption"),
      id: ApiTimeOffPolicyMilestoneCarryoverType.none,
    },
    {
      title: i18n.t("time_off:milestone_carryover_up_to_title"),
      caption: i18n.t("time_off:milestone_carryover_up_to_caption"),
      id: ApiTimeOffPolicyMilestoneCarryoverType.up_to,
    },
    {
      title: i18n.t("time_off:milestone_carryover_unlimited_title"),
      caption: i18n.t("time_off:milestone_carryover_unlimited_caption"),
      id: ApiTimeOffPolicyMilestoneCarryoverType.unlimited,
    },
  ];
};

export enum LeaveTypeRuntimeType {
  regular = "regular",
  parental = "parental",
}

export const getAccrualFreqKeyMap = (): Record<ApiTimeOffAccrualFrequencyTypes, string> => ({
  [ApiTimeOffAccrualFrequencyTypes.daily]: i18n.t("time_off:accrual_freq_daily"),
  [ApiTimeOffAccrualFrequencyTypes.weekly]: i18n.t("time_off:accrual_freq_weekly"),
  [ApiTimeOffAccrualFrequencyTypes.biweekly]: i18n.t("time_off:accrual_freq_biweekly"),
  [ApiTimeOffAccrualFrequencyTypes.semimonthly]: i18n.t("time_off:accrual_freq_semimonthly"),
  [ApiTimeOffAccrualFrequencyTypes.monthly]: i18n.t("time_off:accrual_freq_monthly"),
  [ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional]: i18n.t(
    "time_off:accrual_freq_monthly_annual_proportional"
  ),
  [ApiTimeOffAccrualFrequencyTypes.hire_date_monthly]: i18n.t(
    "time_off:accrual_freq_hire_date_monthly"
  ),
  [ApiTimeOffAccrualFrequencyTypes.quarterly]: i18n.t("time_off:accrual_freq_quarterly"),
  [ApiTimeOffAccrualFrequencyTypes.semiannual]: i18n.t("time_off:accrual_freq_semiannual"),
  [ApiTimeOffAccrualFrequencyTypes.yearly]: i18n.t("time_off:accrual_freq_yearly"),
  [ApiTimeOffAccrualFrequencyTypes.hire_date]: i18n.t("time_off:accrual_freq_yearly_hire_date"),
  [ApiTimeOffAccrualFrequencyTypes.hire_date_monthly_annual_proportional]: i18n.t(
    "time_off:accrual_freq_hire_date_monthly_annual_proportional"
  ),
});

export const getLeaveTypes = (): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:leave_type_regular"),
      id: LeaveTypeRuntimeType.regular,
    },
    {
      title: i18n.t("time_off:leave_type_parental"),
      id: LeaveTypeRuntimeType.parental,
    },
  ];
};

export interface ApiTimeOffPolicyRuleCheck {
  rule: Rule;
  is_matched: boolean;
}

export interface ApiTimeOffPolicyRuleGroupCheck {
  rules: ApiTimeOffPolicyRuleCheck[];
  is_matched: boolean;
}

export interface ApiTimeOffEmployeePolicyCheck {
  policy_id: number;
  title: string;
  is_effective: boolean;
  is_matched: boolean | null;
  groups: ApiTimeOffPolicyRuleGroupCheck[];
  is_assigned: boolean;
}
